import React, { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Space, Button, theme, Menu, Modal, Popover } from "antd";
import {
    LogoutOutlined,
    SettingOutlined,
    BulbOutlined,
    PlusOutlined,
    UserOutlined,
    CrownOutlined,
    BgColorsOutlined,
} from "@ant-design/icons";

import ThemeSelector from "./ThemeSelector";

import { useLogout, useUpdateMe } from "../actions/auth";
import { useSubscription } from "../_actions/mutations/subscription";
import { history } from "../_helpers/history";
import { getPublicImageURL } from "../_helpers/image";
import QurealSpotlight from "../creations/QurealSpotlight";
import { useItemsFine } from "../_actions/app/items";
import TokenCounter from "../finance/TokenCounter";
import FeatureRequestBox from "../feedback/FeatureRequestBox";
import BusinessFormModal from "../business_form/BusinessFormWrapper";
import TokenManagerModal from "../finance/TokenManagerModal";
// import SubscriptionPlan from "../components/commons/SubscriptionPlan";
import PricingModal from "../components/PricingModal";

const SubscriptionButton = ({ subscription, onSubscribeClick }) => {
    const planName = subscription?.payment_plan?.plan_name || "Pro";

    const pillStyle = {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 16px",
        borderRadius: "999px",
        border: "1.5px solid rgba(139, 105, 20, 0.25)",
        background: "linear-gradient(135deg, #fdf6e3 0%, #f5e9c4 100%)",
        color: "#7a5c14",
        fontWeight: 600,
        fontSize: "13.5px",
        letterSpacing: "0.01em",
        cursor: "pointer",
        outline: "none",
        // boxShadow: "0 1px 4px rgba(139, 105, 20, 0.10)",
        // transition: "box-shadow 0.18s ease, background 0.18s ease, transform 0.12s ease",
        whiteSpace: "nowrap",
        fontFamily: "inherit",
    };

    const starStyle = { fontSize: "13px", color: "#8b6914", lineHeight: 1 };

    if (subscription && (subscription.is_active || subscription.is_trial)) {
        const label = subscription.is_trial
            ? `Free Trial · ${planName}`
            : `${planName} · Active`;
        return (
            <button
                style={pillStyle}
                onClick={() => history.push("/settings/billing")}
            >
                <span style={starStyle}>★</span>
                {label}
            </button>
        );
    }

    return (
        <button style={pillStyle} onClick={onSubscribeClick}>
            <span style={starStyle}>★</span>
            Try {planName}
        </button>
    );
};

const truncate = (str, len = 15) => {
    if (!str) return "";
    return str.length > len ? str.slice(0, len) + "..." : str;
};

const { useToken } = theme;

const TopBarOptions = ({ name, orgs, selected_org, isMobile, me = {} }) => {
    const { token } = useToken();
    const { data: subscription } = useSubscription(selected_org?.id);
    const { logout, loading: loadingLogout } = useLogout();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const contentStyle = {
        backgroundColor: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        minWidth: "200px",
    };

    const menuStyle = {
        boxShadow: "none",
    };

    const items = [
        {
            key: "account_settings",
            label: "Account Settings",
            icon: <SettingOutlined />,
        },
        {
            key: "logout",
            label: "Logout",
            icon: <LogoutOutlined />,
            loading: loadingLogout,
        },
    ];

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: isMobile ? 0 : "10px",
            }}
        >
            {!isMobile && (
                <>
                    <SubscriptionButton
                        subscription={subscription}
                        onSubscribeClick={() => setIsModalOpen(true)}
                    />

                    <PricingModal
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSubscribe={(plan) => {
                            setIsModalOpen(false);
                            history.push("/settings/billing");
                        }}
                    />
                </>
            )}
            <QurealSpotlight me={me} />

            {/* <TokenCounter selected_org={selected_org} /> */}
            <TokenManagerModal
                selected_org={selected_org}
                trigger={
                    <TokenCounter
                        selected_org={selected_org}
                        isMobile={isMobile}
                    />
                }
            />

            {!isMobile && (
                <OrgDropdown isMobile={isMobile} selected_org={selected_org} />
            )}

            {!isMobile && (
                <Dropdown
                    menu={{
                        items,
                        onClick: ({ key }) => {
                            if (key === "logout") {
                                logout();
                            } else if (key === "account_settings") {
                                history.push("/settings/update-profile");
                            }
                        },
                    }}
                    popupRender={(menu) => (
                        <div style={contentStyle}>
                            {/* <Divider style={{ margin: 0 }} /> */}
                            <div
                                style={{
                                    padding: "15px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    backgroundColor: "white",
                                }}
                            >
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://app.qureal.com/privacy_policy"
                                >
                                    Privacy Policy
                                </a>
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    href="https://app.qureal.com/terms_and_conditions"
                                >
                                    Terms & Conditions
                                </a>
                            </div>
                            <Menu>
                                <FeatureRequestBox
                                    trigger={
                                        <Menu.Item icon={<BulbOutlined />}>
                                            Feature Request
                                        </Menu.Item>
                                    }
                                />
                            </Menu>
                            {React.cloneElement(menu, { style: menuStyle })}
                        </div>
                    )}
                >
                    <div
                        onClick={(e) => e.preventDefault()}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            // marginTop: "5px",
                        }}
                    >
                        <Space align="center">
                            {isMobile ? (
                                <UserOutlined />
                            ) : (
                                <Button icon={<UserOutlined />}></Button>
                            )}
                        </Space>
                    </div>
                </Dropdown>
            )}
        </div>
    );
};

export default TopBarOptions;

export function OrgDropdown({ selected_org, isMobile }) {
    const updateMeMutation = useUpdateMe();
    const { isSuccess, data } = useItemsFine(
        "qdb_organization",
        `fields=id,name,is_personal,is_external,logo_square&filter=${JSON.stringify(
            {
                _or: [
                    { owner: { _eq: "$CURRENT_USER" } },
                    {
                        _and: [
                            {
                                users: {
                                    directus_users_id: { _eq: "$CURRENT_USER" },
                                },
                            },
                            { users: { role: { _eq: "member" } } },
                        ],
                    },
                ],
            }
        )}`,
        true
    );

    const [options, setOptions] = React.useState([]);
    const [showModal, setShowModal] = React.useState(false);

    useEffect(() => {
        if (isSuccess && data?.length > 0) {
            const out = [];

            data.forEach((item) => {
                out.push({
                    label: (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span>
                                {item.is_personal ? "Personal" : item.name}
                            </span>
                            <SettingOutlined
                                style={{
                                    fontSize: "14px",
                                    marginLeft: "20px",
                                    cursor: "pointer",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    history.push(
                                        `/settings/brand/${item.id}/edit`
                                    );
                                }}
                            />
                        </div>
                    ),
                    key: item.id,
                    icon: (
                        <img
                            src={getPublicImageURL(item.logo_square)}
                            alt="logo"
                            style={{
                                height: "30px",
                                width: "30px",
                                marginRight: "10px",
                                objectFit: "contain",
                            }}
                        />
                    ),
                });
            });

            out.push({
                label: "Add New Organization",
                key: "create",
                icon: <PlusOutlined />,
            });

            setOptions(out);
        }
    }, [isSuccess, data]);

    const handleMenuClick = ({ key }) => {
        if (key === "create") {
            setShowModal(true);
        } else {
            updateMeMutation.mutate({ selected_org: key });
        }
    };

    return (
        <>
            <Dropdown
                menu={{
                    items: options,
                    onClick: handleMenuClick,
                }}
                placement={isMobile ? "topLeft" : "bottomLeft"}
            >
                <div
                    onClick={(e) => e.preventDefault()}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Space align="center">
                        {isMobile ? (
                            <img
                                src={getPublicImageURL(
                                    selected_org?.logo_square
                                )}
                                alt="logo"
                                style={{
                                    width: "25px",
                                    height: "25px",
                                    objectFit: "contain",
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                }}
                            />
                        ) : (
                            <Button
                                icon={
                                    <img
                                        src={getPublicImageURL(
                                            selected_org?.logo_square
                                        )}
                                        alt="logo"
                                        style={{
                                            width: "auto",
                                            height: "20px",
                                            margin: "-5px 0",
                                            objectFit: "contain",
                                        }}
                                    />
                                }
                                loading={updateMeMutation.isLoading}
                                // style={{ marginTop: "20px" }}
                            >
                                {selected_org?.is_personal
                                    ? "Personal"
                                    : truncate(selected_org?.name)}

                                <DownOutlined
                                    style={{ fontSize: ".9em", color: "#aaa" }}
                                />
                            </Button>
                        )}
                    </Space>
                </div>
            </Dropdown>

            <BusinessFormModal
                open={showModal}
                onCancel={() => setShowModal(false)}
            />
        </>
    );
}
